import React, { useState } from 'react';
import { t } from '../lib/i18n';
import { Link, useLocation } from 'react-router-dom';
import { Home, LayoutGrid, Store, Search, ChevronDown, ShoppingCart } from 'lucide-react';
import UserMenu from './UserMenu';
import { ThemeToggle } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useShoppingList } from '../context/ShoppingListContext';
import CategoryGrid from './CategoryGrid';
import MobileTabBar, { openMobileSearch } from './MobileTabBar';

const ListLink = () => {
  const list = useShoppingList();
  const active = useLocation().pathname === '/liste';
  const count = list?.count || 0;
  return (
    <Link to="/liste" aria-label="Liste de courses" data-testid="nav-shopping-list" title={t('Ma liste')}
      className={`relative flex items-center justify-center w-10 h-10 rounded-full transition-colors ${active ? 'bg-[#E50914] text-white' : 'text-white hover:bg-white/10'}`}>
      <ShoppingCart size={21} />
      {count > 0 && <span key={count} className="count-pop absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E50914] text-white text-[10px] font-black flex items-center justify-center border-2 border-black" data-testid="nav-shopping-list-count">{count > 99 ? '99+' : count}</span>}
    </Link>
  );
};

const Navbar = ({ onCategorySelect, activeCategory, categories = [], categoryCounts, searchQuery = '', onSearchChange = () => {} }) => {
  const { user, openAuth, logout } = useAuth();
  const isStores = useLocation().pathname.startsWith('/magasins');
  const setSearchQuery = onSearchChange;
  const [showCategories, setShowCategories] = useState(false);

  return (
    <>
      {/* Sticky Header */}
      <header data-testid="navbar" className="sticky top-0 z-50 bg-black/80 backdrop-blur-md border-b border-[#262626] rounded-t-lg">
        <div className="max-w-[1200px] mx-auto px-4">
          <div className="flex items-center justify-between h-16 gap-4">
            {/* Logo */}
            <a href="/" className="flex-shrink-0 flex items-center" data-testid="navbar-logo" aria-label="DealRun">
              <img src="/logo-dealrun.png" alt="DealRun" className="h-10 w-auto logo-dark" />
              <img src="/logo-dealrun-light.png" alt="DealRun" className="h-10 w-auto logo-light" />
            </a>

            {/* Nav Links - Desktop */}
            <nav className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all active:scale-95 ${!isStores ? 'bg-[#E50914] text-white hover:bg-[#F40612]' : 'text-[#B3B3B3] hover:text-white hover:bg-white/10'}`}
                data-testid="nav-home"
              >
                <Home size={16} />
              </Link>
              <button
                onClick={() => setShowCategories(!showCategories)}
                className="flex items-center gap-2 px-4 py-2 rounded-md text-[#B3B3B3] text-sm font-semibold transition-all hover:text-white hover:bg-white/10"
                data-testid="nav-categories-toggle"
              >
                <LayoutGrid size={16} />
                {t('CATÉGORIES')}
                <ChevronDown size={14} className={`transition-transform ${showCategories ? 'rotate-180' : ''}`} />
              </button>
              <Link
                to="/magasins"
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${isStores ? 'bg-[#E50914] text-white hover:bg-[#F40612]' : 'text-[#B3B3B3] hover:text-white hover:bg-white/10'}`}
                data-testid="nav-magasins"
              >
                <Store size={16} />
                {t('MAGASINS')}
              </Link>
            </nav>

            {/* Search Bar */}
            <div className="hidden md:flex flex-1 max-w-md">
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder={t('Rechercher un produit ou un magasin')}
                  value={searchQuery}
                  autoFocus={Boolean(searchQuery)}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-4 pr-10 py-2 rounded-sm bg-[#141414] border border-[#262626] text-white text-sm placeholder-[#808080] focus:outline-none focus:border-[#E50914] transition-colors"
                  data-testid="search-input"
                />
                <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#808080]" />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} data-testid="search-clear-btn"
                    className="absolute right-9 top-1/2 -translate-y-1/2 text-[#808080] hover:text-white text-xs font-bold">✕</button>
                )}
              </div>
            </div>

            <ThemeToggle />
            <ListLink />
            <UserMenu />

            <button onClick={openMobileSearch} aria-label={t('Rechercher')} data-testid="mobile-search-toggle"
              className={`md:hidden flex items-center justify-center w-10 h-10 rounded-full transition-colors ${searchQuery ? 'bg-[#E50914] text-white' : 'text-white hover:bg-white/10'}`}>
              <Search size={22} />
            </button>
          </div>
        </div>

        {/* Categories Dropdown */}
        {showCategories && (
          <div className="hidden md:block absolute top-16 left-0 right-0 bg-[#0b0b0b] border-b border-[#262626] shadow-[0_30px_60px_rgba(0,0,0,0.5)] z-40" data-testid="categories-dropdown">
            <CategoryGrid categories={categories} activeCategory={activeCategory} counts={categoryCounts}
              onSelect={(slug) => { onCategorySelect && onCategorySelect(slug); setShowCategories(false); }} />
          </div>
        )}

      </header>
      <MobileTabBar categories={categories} activeCategory={activeCategory} onCategorySelect={onCategorySelect} categoryCounts={categoryCounts} searchQuery={searchQuery} onSearchChange={onSearchChange} />

      {/* Click outside to close categories */}
      {showCategories && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setShowCategories(false)}
        />
      )}
    </>
  );
};

export default Navbar;
